// components/industry-templates/ElectricVehiclesEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Zap, Car, Battery, TrendingUp } from 'lucide-react';

export const ElectricVehiclesEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* EV Showcase */}
      {industryData?.ev_showcase && (
        <Card className="border-0 bg-gradient-to-br from-green-500/20 to-teal-500/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <Zap className="w-8 h-8" style={{ color: theme.primary }} />
              Electric Vehicle Showcase
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {industryData.ev_showcase.map((vehicle: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl border-2" style={{ borderColor: theme.primary }}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-1">{vehicle.model}</h3>
                      <p className="text-slate-300">{vehicle.manufacturer}</p>
                    </div>
                    {vehicle.new_launch && (
                      <Badge className="bg-yellow-500 text-black">NEW</Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-4">
                    <div className="bg-white/5 rounded p-2 text-center">
                      <p className="text-xs text-slate-400">Range</p>
                      <p className="font-bold text-white text-sm">{vehicle.range}</p>
                    </div>
                    <div className="bg-white/5 rounded p-2 text-center">
                      <p className="text-xs text-slate-400">Charge Time</p>
                      <p className="font-bold text-white text-sm">{vehicle.charge_time}</p>
                    </div>
                    <div className="bg-white/5 rounded p-2 text-center">
                      <p className="text-xs text-slate-400">Price</p>
                      <p className="font-bold text-white text-sm">{vehicle.price}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Battery Technology */}
      {industryData?.battery_tech && (
        <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(20, 184, 166, 0.1)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Battery className="w-6 h-6" style={{ color: theme.primary }} />
              Battery Technology Innovations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {industryData.battery_tech.map((tech: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl">
                  <h3 className="text-xl font-bold text-white mb-2">{tech.name}</h3>
                  <p className="text-slate-300 text-sm mb-3">{tech.description}</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white/5 rounded p-3 text-center">
                      <p className="text-xs text-slate-400">Capacity</p>
                      <p className="font-bold" style={{ color: theme.primary }}>{tech.capacity}</p>
                    </div>
                    <div className="bg-white/5 rounded p-3 text-center">
                      <p className="text-xs text-slate-400">Life Cycle</p>
                      <p className="font-bold text-white">{tech.lifecycle}</p>
                    </div>
                    <div className="bg-white/5 rounded p-3 text-center">
                      <p className="text-xs text-slate-400">Cost</p>
                      <p className="font-bold text-white">{tech.cost}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charging Infrastructure */}
      {industryData?.charging_infrastructure && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Car className="w-6 h-6" style={{ color: theme.primary }} />
              Charging Infrastructure
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {industryData.charging_infrastructure.map((infra: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4" style={{ borderColor: theme.primary }}>
                  <h4 className="font-bold text-white mb-2">{infra.type}</h4>
                  <p className="text-sm text-slate-400 mb-2">{infra.provider}</p>
                  <div className="flex gap-2">
                    <Badge style={{ backgroundColor: theme.primary }}>{infra.power}</Badge>
                    <Badge variant="outline" className="border-white/20 text-white">{infra.charging_time}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Market Trends */}
      {industryData?.market_analysis && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-6 h-6" style={{ color: theme.primary }} />
              EV Market Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-white/5 rounded-lg">
                <p className="text-3xl font-bold" style={{ color: theme.primary }}>
                  {industryData.market_analysis.growth_rate}
                </p>
                <p className="text-xs text-slate-400">Annual Growth</p>
              </div>
              <div className="text-center p-4 bg-white/5 rounded-lg">
                <p className="text-3xl font-bold" style={{ color: theme.primary }}>
                  {industryData.market_analysis.market_size}
                </p>
                <p className="text-xs text-slate-400">Market Size</p>
              </div>
              <div className="text-center p-4 bg-white/5 rounded-lg">
                <p className="text-3xl font-bold" style={{ color: theme.primary }}>
                  {industryData.market_analysis.forecast}
                </p>
                <p className="text-xs text-slate-400">2030 Forecast</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
